import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import HeroDither from '../components/shared/HeroDither';
import Reveal, { REVEAL_CSS } from '../components/shared/Reveal';
import useSeoMeta from '../components/shared/useSeoMeta';
import RelatedPagesLinks from '../components/shared/RelatedPagesLinks';

const IconArrowLeft = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconTap = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11a3 3 0 106 0 3 3 0 00-6 0z"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>
  </svg>
);

const IconLink = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.07 0l2.83-2.83a5 5 0 00-7.07-7.07L11.5 4.5"/><path d="M14 11a5 5 0 00-7.07 0L4.1 13.83a5 5 0 007.07 7.07L12.5 19.5"/>
  </svg>
);

const IconWalletMini = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 010-4h14v4"/><path d="M3 5v14a2 2 0 002 2h16v-5"/><path d="M18 12a2 2 0 000 4h4v-4z"/>
  </svg>
);

const IconFile = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/>
  </svg>
);

const IconBank = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M5 21V10M19 21V10M3 10l9-6 9 6M8 10v11M16 10v11"/>
  </svg>
);

const MAIN_CARDS = [
  { icon: <IconTap/>, title: 'Un toque, sin apps', desc: 'NFC o QR comparten tu perfil completo al instante. Quien te conoce no necesita instalar nada en su celular.' },
  { icon: <IconLink/>, title: 'Siempre la versión vigente', desc: 'Tu link no cambia nunca. Si actualizas tu cargo, tu empresa o tu número, todos los que ya te guardaron ven la información nueva.' },
  { icon: <IconWalletMini/>, title: 'Apple Wallet y Google Wallet', desc: 'Guarda tu perfil en la billetera nativa del celular, sin depender de tener la app de Contact abierta.' },
  { icon: <IconFile/>, title: 'Archivos descargables', desc: 'Adjunta un catálogo o una presentación en PDF para que la descarguen directo desde tu perfil.' },
  { icon: <IconBank/>, title: 'Datos de transferencia', desc: 'Comparte tus datos bancarios con un botón de copiar todo, igual que en las apps de los bancos chilenos.' },
];

const TEMPLATES = [
  { name: 'Clásica', desc: 'La estructura de tarjeta de presentación tradicional, pensada para que la información se lea rápido.' },
  { name: 'Corporativa', desc: 'Con tu logo y los colores de tu empresa al frente, para equipos que representan una marca.' },
  { name: 'Retrato', desc: 'Tu foto a ancho completo con un panel de color debajo, una presentación más personal.' },
];

const STEPS = [
  { num: '01', title: 'Activa tu perfil', desc: 'Sin configuración técnica. Creas tu cuenta y tu perfil ya está listo para compartirse.' },
  { num: '02', title: 'Personaliza tu información', desc: 'Agrega tus redes, tu información de contacto y tu marca personal en minutos.' },
  { num: '03', title: 'Comparte con un toque', desc: 'Acerca tu perfil a cualquier celular y tu información queda guardada al instante.' },
];

const FAQS = [
  { q: '¿Necesito una app para que alguien vea mi perfil?', a: 'No. Comparten el link o acercan el NFC y el perfil se abre en cualquier navegador, sin instalar nada.' },
  { q: '¿Puedo tener más de un perfil?', a: 'Sí, según tu plan puedes administrar varios perfiles desde la misma cuenta.' },
  { q: '¿Qué pasa si cambio mi información?', a: 'Se actualiza al instante. El link que ya compartiste sigue siendo el mismo y muestra la versión vigente.' },
];

const PAGE_TITLE = 'Tarjeta de Presentación Digital NFC | Contact';
const PAGE_DESCRIPTION = 'Comparte tu perfil profesional con un toque NFC o QR. Plantillas personalizables, Apple Wallet y Google Wallet incluidos. Crea tu tarjeta digital gratis.';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const TarjetaDigitalPage = () => {
  const navigate = useNavigate();
  useSeoMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: '/tarjeta-digital' });

  return (
    <div className="td-root">
      <HeroDither/>
      <header className="td-nav">
        <div className="td-nav-inner">
          <ContactLogo clickable/>
          <div className="td-nav-actions">
            <button onClick={() => navigate('/')} className="td-btn-back">
              <IconArrowLeft/> Volver
            </button>
            <button onClick={() => navigate('/comprar')} className="td-btn-comprar-nav">Comprar</button>
            <button onClick={() => navigate('/login')} className="td-btn-acceder">Acceder</button>
          </div>
        </div>
      </header>

      <section className="td-hero">
        <div className="td-container">
          <p className="td-eyebrow">Tarjeta y perfil digital</p>
          <h1 className="td-h1">Tu identidad profesional, disponible en un toque.</h1>
          <p className="td-lead">
            Un solo perfil reemplaza la tarjeta de papel, el currículum que mandas por correo y el
            mensaje con tus datos copiados y pegados. Lo compartes una vez y queda disponible para
            siempre, siempre actualizado.
          </p>
        </div>
      </section>

      <section className="td-section">
        <div className="td-container">
          <Reveal>
          <h2 className="td-h2">Hecho para compartirse cientos de veces, sin fricción.</h2>
          <div className="td-grid">
            {MAIN_CARDS.map((c) => (
              <div key={c.title} className="td-card">
                <span className="td-card-icon">{c.icon}</span>
                <h3 className="td-card-title">{c.title}</h3>
                <p className="td-card-desc">{c.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="td-section">
        <div className="td-container">
          <Reveal>
          <h2 className="td-h2 td-h2--tight">Tres plantillas, cada una con su propio carácter.</h2>
          <div className="td-template-grid">
            {TEMPLATES.map((t) => (
              <div key={t.name} className="td-template-card">
                <h4 className="td-template-name">{t.name}</h4>
                <p className="td-template-desc">{t.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="td-section">
        <div className="td-container">
          <Reveal>
          <h2 className="td-h2 td-h2--tight">Cómo funciona, de principio a fin.</h2>
          <div className="td-steps-grid">
            {STEPS.map((s) => (
              <div key={s.num} className="td-step">
                <span className="td-step-num">{s.num}</span>
                <h4 className="td-step-title">{s.title}</h4>
                <p className="td-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="td-section">
        <div className="td-container">
          <Reveal>
          <h2 className="td-h2 td-h2--tight">Preguntas frecuentes.</h2>
          <div className="td-faq-list">
            {FAQS.map((f) => (
              <div key={f.q} className="td-faq-item">
                <h4 className="td-faq-q">{f.q}</h4>
                <p className="td-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="td-cta">
        <div className="td-container td-cta-inner">
          <h2 className="td-h2">Crea tu perfil digital, gratis.</h2>
          <p className="td-p td-cta-sub">Sin tarjeta de crédito. Listo para compartir en menos de cinco minutos.</p>
          <div className="td-cta-actions">
            <button onClick={() => navigate('/register')} className="td-btn-pill">
              Comenzar gratis <IconArrowRight/>
            </button>
            <button onClick={() => navigate('/cotizar')} className="td-btn-outline">
              Hablar con ventas
            </button>
          </div>
        </div>
      </section>

      <footer className="td-footer">
        <div className="td-container">
          <div className="td-related-row">
            <RelatedPagesLinks currentPath="/tarjeta-digital" linkClassName="td-related-link" />
          </div>
          <div className="td-footer-inner">
            <ContactLogo/>
            <span className="td-footer-copy">© 2026 CONTACT Network SPA. Chile</span>
          </div>
        </div>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <style>{CSS}</style>
      <style>{REVEAL_CSS}</style>
    </div>
  );
};

const CSS = `
:root{
  --td-bg:#F9F9F8;
  --td-bg-w:#FFFFFF;
  --td-text:#000000;
  --td-muted:#4a4a4a;
  --td-faint:#C0C0C0;
  --td-border:#EEEEEC;
  --td-border-dk:#CCCCCA;
}

.td-root{font-family:'abcNormal','Inter','Arial',sans-serif;background:var(--td-bg);color:var(--td-text);min-height:100vh;overflow-x:hidden;line-height:1.6;position:relative}
.nx-hero-dither{position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:.27;-webkit-mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);transform:translateZ(0);will-change:transform}

.td-nav{position:sticky;top:0;z-index:100;background:transparent}
.td-nav-inner{max-width:900px;margin:0 auto;padding:0 40px;height:64px;display:flex;align-items:center;justify-content:space-between}
.td-btn-back{display:inline-flex;align-items:center;gap:7px;background:none;border:none;color:var(--td-muted);font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.td-btn-back:hover{color:var(--td-text)}
.td-nav-actions{display:flex;align-items:center;gap:10px}
.td-btn-acceder{display:inline-flex;align-items:center;background:var(--td-text);color:#fff;border:none;border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s}
.td-btn-acceder:hover{opacity:.82}
.td-btn-comprar-nav{display:inline-flex;align-items:center;background:transparent;color:var(--td-text);border:1.5px solid var(--td-border-dk);border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s}
.td-btn-comprar-nav:hover{border-color:var(--td-text)}

.td-container{max-width:900px;margin:0 auto;padding:0 40px}

.td-hero{padding:72px 0 56px}
.td-eyebrow{font-family:'Inter',system-ui,sans-serif;font-size:11px;font-weight:700;color:var(--td-muted);text-transform:uppercase;letter-spacing:.08em;margin-bottom:20px}
.td-h1{font-weight:300;font-size:clamp(32px,4.6vw,52px);line-height:1.15;letter-spacing:-.025em;color:var(--td-text);margin-bottom:26px;max-width:720px}
.td-lead{font-family:'Inter',system-ui,sans-serif;font-size:clamp(15px,1.6vw,17px);color:var(--td-muted);line-height:1.75;max-width:600px}

.td-section{padding:0 0 80px}
.td-h2{font-weight:300;font-size:clamp(22px,2.6vw,30px);line-height:1.3;letter-spacing:-.02em;color:var(--td-text);margin-bottom:32px;max-width:620px}
.td-h2--tight{margin-bottom:28px}
.td-p{font-family:'Inter',system-ui,sans-serif;font-size:15px;color:var(--td-muted);line-height:1.75;max-width:560px}

.td-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:var(--td-border);border:1px solid var(--td-border)}
.td-card{background:var(--td-bg-w);padding:26px}
.td-card-icon{display:inline-flex;color:var(--td-text);margin-bottom:14px}
.td-card-title{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--td-text);margin-bottom:8px;letter-spacing:-.01em}
.td-card-desc{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--td-muted);line-height:1.65}

.td-template-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.td-template-card{background:var(--td-bg-w);border:1px solid var(--td-border);border-radius:14px;padding:24px}
.td-template-name{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;color:var(--td-text);margin-bottom:8px}
.td-template-desc{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--td-muted);line-height:1.65}

.td-steps-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
.td-step{border-top:2px solid var(--td-text);padding-top:18px}
.td-step-num{font-family:'Inter',system-ui,sans-serif;font-size:12px;font-weight:700;color:var(--td-muted);letter-spacing:.05em}
.td-step-title{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;color:var(--td-text);margin:10px 0 8px}
.td-step-desc{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--td-muted);line-height:1.65}

.td-faq-list{display:flex;flex-direction:column;gap:22px;max-width:640px}
.td-faq-item{border-top:1px solid var(--td-border);padding-top:16px}
.td-faq-q{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--td-text);margin-bottom:8px}
.td-faq-a{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--td-muted);line-height:1.65}

.td-cta{padding:0 0 80px}
.td-cta-inner{background:var(--td-bg-w);border:1px solid var(--td-border);border-radius:24px;padding:60px 48px;text-align:center;display:flex;flex-direction:column;align-items:center}
.td-cta-sub{text-align:center;margin:0 auto 32px}
.td-cta-actions{display:flex;align-items:center;gap:14px}
.td-btn-pill{display:inline-flex;align-items:center;gap:8px;background:var(--td-text);color:#fff;border:none;border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1);white-space:nowrap}
.td-btn-pill:hover{opacity:.82;transform:translateY(-1px)}
.td-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:var(--td-text);border:1.5px solid var(--td-border-dk);border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap}
.td-btn-outline:hover{border-color:var(--td-text);background:var(--td-bg)}

.td-related-row{display:flex;flex-wrap:wrap;gap:10px;padding:28px 0;border-top:1px solid var(--td-border)}
.td-related-link{background:none;border:1px solid var(--td-border-dk);border-radius:100px;padding:7px 15px;color:var(--td-muted);font-family:'Inter',system-ui,sans-serif;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s}
.td-related-link:hover{color:var(--td-text);border-color:var(--td-text)}

.td-footer-inner{display:flex;align-items:center;justify-content:space-between;padding:24px 0}
.td-footer-copy{font-family:'Inter',system-ui,sans-serif;font-size:12px;color:var(--td-faint)}

@media(max-width:640px){
  .td-nav-inner,.td-container{padding:0 20px}
  .td-nav-actions{gap:6px}
  .td-btn-acceder,.td-btn-comprar-nav{height:32px;padding:0 12px;font-size:12px}
  .td-btn-back{font-size:0}
  .td-hero{padding:48px 0 40px}
  .td-section,.td-cta{padding-bottom:56px}
  .td-grid,.td-template-grid,.td-steps-grid{grid-template-columns:1fr}
  .td-cta-inner{padding:44px 22px}
  .td-cta-actions{flex-direction:column;width:100%}
  .td-cta-actions .td-btn-pill,.td-cta-actions .td-btn-outline{width:100%;justify-content:center}
  .td-footer-inner{flex-direction:column;gap:8px;text-align:center}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default TarjetaDigitalPage;
